import { AppContext } from '@/common/AppContext';
import { track } from '@/common/track';
import { Button } from '@/components/Button';
import { Paper } from '@/components/Paper';
import { CreateJobModePicker } from '@/components/job/CreateJobModePicker';
import { useLocale } from '@/providers/LocaleProvider';
import ArrowRightIcon from '@/resources/images/icons-arrow-right.svg';
import { KLAYO_COLORS } from '@/themes';
import { Box, Fade, Stack, Typography } from '@mui/material';
import { ManageJobsEvent } from 'klayowebshared';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JobBuilderDescriptionSection } from '../job-builder/JobBuilderDescriptionSection';
import { JobBuilderKISection } from '../job-builder/JobBuilderKISection';
import { JobBuilderScratchSection } from '../job-builder/JobBuilderScratchSection';
import {
  BuildMode,
  DescriptionButtonConfig,
  JobBuilderSectionRef,
} from '../job-builder/jobBuilder.types';
import SettingStandardsLibraryJobDetail from './SettingStandardsLibraryJobDetail';

interface SettingsJobBuilderViewProps {
  theme: any;
  /** Refetch the jobs list after a job is imported (this parent view doesn't remount on navigate). */
  onImported?: () => void;
}

export function SettingsJobBuilderView({ theme, onImported }: SettingsJobBuilderViewProps) {
  const { translate } = useLocale();
  const navigate = useNavigate();
  const { organization, user } = useContext(AppContext);

  const isTrial =
    !user?.isFullAccess ||
    !!organization?.isTrial ||
    !organization?.enabledFeatures?.KlayoIntelligence;

  const [mode, setMode] = useState<BuildMode>(BuildMode.KI);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [hasKISelection, setHasKISelection] = useState(false);
  const [descriptionButtonConfig, setDescriptionButtonConfig] =
    useState<DescriptionButtonConfig | null>(null);

  const kiRef = useRef<JobBuilderSectionRef | null>(null);
  const descriptionRef = useRef<JobBuilderSectionRef | null>(null);
  const scratchRef = useRef<JobBuilderSectionRef | null>(null);

  const eventForMode = (m: BuildMode) =>
    m === BuildMode.KI
      ? ManageJobsEvent.NewJobFromIntelligence
      : m === BuildMode.DESCRIPTION
        ? ManageJobsEvent.NewJobFromJd
        : ManageJobsEvent.NewJobFromScratch;

  // KI is the default mode on open, so fire its "new job from" event once on mount.
  useEffect(() => {
    track(ManageJobsEvent.NewJobFromIntelligence);
  }, []);

  const handleModeChange = (newMode: BuildMode) => {
    if (newMode !== mode) track(eventForMode(newMode));
    setMode(newMode);
  };

  const getActiveRef = () => {
    switch (mode) {
      case BuildMode.KI:
        return kiRef;
      case BuildMode.DESCRIPTION:
        return descriptionRef;
      case BuildMode.SCRATCH:
        return scratchRef;
    }
  };

  const handleCancel = () => navigate('/settings/jobs');

  const handleContinue = async () => {
    if (mode === BuildMode.SCRATCH) {
      navigate('/settings/jobs/new');
      return;
    }
    const jobId = await getActiveRef().current?.submit();
    if (jobId) {
      setSelectedJobId(jobId);
      setIsDetailOpen(true);
    }
  };

  const getContinueLabel = () => {
    switch (mode) {
      case BuildMode.KI:
        return translate('jobBuilder.ReviewAndImport');
      case BuildMode.DESCRIPTION:
        return descriptionButtonConfig?.label ?? translate('jobBuilder.continue');
      default:
        return translate('jobBuilder.continue');
    }
  };

  const isContinueDisabled = () => {
    if (mode === BuildMode.KI) return !hasKISelection;
    if (mode === BuildMode.DESCRIPTION) return descriptionButtonConfig?.disabled ?? false;
    return false;
  };

  return (
    <Paper
      sx={{
        flex: 1,
        p: { xs: '24px', md: '56px 64px' },
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      <Box sx={{ flex: 1 }} display={'flex'} flexDirection={'column'}>
        <Stack gap={5} flex={1}>
          <Typography variant='h5' fontWeight={600} color={KLAYO_COLORS.GreyGR900}>
            {translate('addEditJob.createJob')}
          </Typography>

          <CreateJobModePicker
            selectedMode={mode}
            onModeChange={handleModeChange}
            translate={translate}
          >
            <Box
              sx={{
                display: mode === BuildMode.KI ? 'flex' : 'none',
                flex: 1,
                flexDirection: 'column',
              }}
            >
              <JobBuilderKISection ref={kiRef} onSelectionChange={setHasKISelection} />
            </Box>
            <Box
              sx={{
                display: mode === BuildMode.DESCRIPTION ? 'flex' : 'none',
                flex: 1,
                flexDirection: 'column',
              }}
            >
              <JobBuilderDescriptionSection
                ref={descriptionRef}
                isActive={mode === BuildMode.DESCRIPTION}
                onButtonConfigChange={setDescriptionButtonConfig}
              />
            </Box>
            <Box
              sx={{
                display: mode === BuildMode.SCRATCH ? 'flex' : 'none',
                flex: 1,
                flexDirection: 'column',
              }}
            >
              <JobBuilderScratchSection ref={scratchRef} />
            </Box>
          </CreateJobModePicker>
        </Stack>
      </Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Button variant='outlined' label={translate('jobBuilder.cancel')} onClick={handleCancel} />
        <Button
          variant='filled'
          label={getContinueLabel()}
          endIcon={<ArrowRightIcon />}
          onClick={handleContinue}
          disabled={isContinueDisabled()}
          isLoading={!!descriptionButtonConfig?.loading}
        />
      </Stack>

      <Fade in={isDetailOpen} timeout={300} unmountOnExit>
        <div>
          <SettingStandardsLibraryJobDetail
            theme={theme}
            open={isDetailOpen}
            onClose={() => setIsDetailOpen(false)}
            jobId={selectedJobId}
            onCloseAllDialog={() => setIsDetailOpen(false)}
            isTrial={isTrial}
            // After a Klayo Intelligence job is imported, refetch the jobs list (this view doesn't
            // remount on navigate) and land the user on the Jobs library.
            onImportCallback={() => {
              if (mode === BuildMode.KI) {
                onImported?.();
                navigate('/settings/jobs');
              }
            }}
          />
        </div>
      </Fade>
    </Paper>
  );
}
